"use client";

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
  type ElementType
} from "react";

import {
  Tabs as ChakraTabs,
  type TabsRootProps,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { Icon } from "../internal/exports";
import { asSlotComponent } from "../internal/v3-slot";

const TabsTrigger = asSlotComponent(ChakraTabs.Trigger);
const TabsContent = asSlotComponent(ChakraTabs.Content);
const TabsContentGroup = asSlotComponent(ChakraTabs.ContentGroup);

const TAB_VALUE_PREFIX = "tab-";
const FIRST_TAB_VALUE = `${TAB_VALUE_PREFIX}0`;

interface TabsCompatContextValue {
  registerTab: () => string;
  registerPanel: () => string;
}

const TabsCompatContext = createContext<TabsCompatContextValue | null>(null);

function useTabsCompatContext(component: string) {
  const ctx = useContext(TabsCompatContext);
  if (!ctx) {
    throw new Error(`${component} must be used within Tabs`);
  }
  return ctx;
}

type OmittedTabsRootProps = "size" | "variant";

export interface TabsProps extends Omit<TabsRootProps, OmittedTabsRootProps> {
  variant?: TabsRootProps["variant"] | "secondary" | "soft-rounded" | string;
  size?: TabsRootProps["size"] | string;
  children?: ReactNode;
}

export function Tabs({
  children,
  colorPalette = "gray",
  value,
  defaultValue,
  ...props
}: TabsProps) {
  const tabCounter = useRef(0);
  const panelCounter = useRef(0);
  tabCounter.current = 0;
  panelCounter.current = 0;

  const ctx = useMemo<TabsCompatContextValue>(
    () => ({
      registerTab: () => `${TAB_VALUE_PREFIX}${tabCounter.current++}`,
      registerPanel: () => `${TAB_VALUE_PREFIX}${panelCounter.current++}`
    }),
    []
  );

  // Compound Tab children auto-register `tab-0`, `tab-1`, … values, so select
  // the first tab by default when neither value nor defaultValue is provided.
  const resolvedDefault =
    value === undefined && defaultValue === undefined
      ? FIRST_TAB_VALUE
      : defaultValue;

  return (
    <TabsCompatContext.Provider value={ctx}>
      <ChakraTabs.Root
        colorPalette={colorPalette}
        value={value}
        defaultValue={resolvedDefault}
        {...(props as TabsRootProps)}
      >
        {children}
      </ChakraTabs.Root>
    </TabsCompatContext.Provider>
  );
}

export interface TabListProps extends HTMLChakraProps<"div"> {
  children?: ReactNode;
}

export function TabList({ children, ...props }: TabListProps) {
  return <ChakraTabs.List {...props}>{children}</ChakraTabs.List>;
}

export interface TabProps extends HTMLChakraProps<"button"> {
  children?: ReactNode;
  value?: string;
  leftIcon?: ElementType;
  rightIcon?: ElementType;
  iconSpacing?: number | string;
}

export function Tab({
  value,
  leftIcon,
  rightIcon,
  iconSpacing,
  children,
  ...props
}: TabProps) {
  const { registerTab } = useTabsCompatContext("Tab");
  const tabValue = value ?? registerTab();

  const content = (
    <>
      {leftIcon && <Icon as={leftIcon} marginEnd={iconSpacing || 2} />}
      {children}
      {rightIcon && <Icon as={rightIcon} marginStart={iconSpacing || 2} />}
    </>
  );

  return (
    <TabsTrigger value={tabValue} {...props}>
      {leftIcon || rightIcon ? content : children}
    </TabsTrigger>
  );
}

export interface TabPanelsProps extends HTMLChakraProps<"div"> {
  children?: ReactNode;
}

export function TabPanels({ children, ...props }: TabPanelsProps) {
  return (
    <TabsContentGroup width="100%" {...props}>
      {children}
    </TabsContentGroup>
  );
}

export interface TabPanelProps extends HTMLChakraProps<"div"> {
  children?: ReactNode;
  value?: string;
}

export function TabPanel({ value, children, ...props }: TabPanelProps) {
  const { registerPanel } = useTabsCompatContext("TabPanel");
  const panelValue = value ?? registerPanel();

  return (
    <TabsContent value={panelValue} width="100%" {...props}>
      {children}
    </TabsContent>
  );
}
